import type {
  LobAggregate,
  PlayerStatistics,
  TeamInfo,
} from '@/types/statistics';
import { SHOT_TYPE_CATALOG } from './shotTypes';

export type SelectionMode = 'players' | 'teams';

export interface SelectionContext {
  selectedIds: string[];
  mode: SelectionMode;
  teams: Record<string, TeamInfo>;
  players: Record<string, PlayerStatistics>;
}

/** Resolve displayed entities to the original player records used by analytics. */
export function resolveSelectedPlayerIds({
  selectedIds,
  mode,
  teams,
  players,
}: SelectionContext): string[] {
  const ids =
    mode === 'teams'
      ? selectedIds.flatMap((teamId) => teams[teamId]?.teammates ?? [])
      : selectedIds;
  return [...new Set(ids)].filter((id) => !!players[id]);
}

export type SpeedState = 'unavailable' | 'limited' | 'normal';

export interface ShotSpeedRow {
  type: string;
  value: number | null;
  measurements: number;
  state: SpeedState;
}

type SpeedRecord = {
  n_with_speed?: number;
  speed_sum_km_h?: number;
};

const speedRecord = (
  ps: PlayerStatistics,
  keys: string[],
): SpeedRecord | null => {
  const speed = ps.shots_tracking?.speed;
  if (!speed) return null;
  const records = keys
    .map((key) => speed[key])
    .filter(
      (record): record is NonNullable<typeof record> =>
        record !== null && record !== undefined,
    );
  if (!records.length) return null;
  return {
    n_with_speed: records.reduce(
      (sum, record) => sum + (record.n_with_speed ?? 0),
      0,
    ),
    speed_sum_km_h: records.reduce(
      (sum, record) => sum + (record.speed_sum_km_h ?? 0),
      0,
    ),
  };
};

/** Weighted shot-speed rows. Missing types remain in data for shared ordering. */
export function buildShotSpeedRows(
  playerIds: string[],
  players: Record<string, PlayerStatistics>,
): ShotSpeedRow[] | null {
  if (!playerIds.some((id) => !!players[id]?.shots_tracking?.speed))
    return null;
  const rows: ShotSpeedRow[] = SHOT_TYPE_CATALOG.map((type) => {
    const records = playerIds
      .map((id) => speedRecord(players[id], type.backendKeys))
      .filter((record): record is SpeedRecord => !!record);
    const measurements = records.reduce(
      (sum, record) => sum + (record.n_with_speed ?? 0),
      0,
    );
    const sum = records.reduce(
      (total, record) => total + (record.speed_sum_km_h ?? 0),
      0,
    );
    const value =
      measurements > 0 && Number.isFinite(sum) ? sum / measurements : null;
    return {
      type: type.key,
      value,
      measurements,
      state:
        measurements === 0
          ? 'unavailable'
          : measurements <= 3
            ? 'limited'
            : 'normal',
    };
  });
  return rows.some((row) => row.value !== null) ? rows : null;
}

export type LobSelection = 'both' | 'forehand_lob' | 'backhand_lob';
export type ThresholdState = 'unavailable' | 'compatible' | 'incompatible';

export interface LobAnalytics {
  hasData: boolean;
  availability: 'none' | 'partial' | 'complete';
  averageHeightM: number | null;
  averageDepthM: number | null;
  behindServiceLinePct: number | null;
  inFrontServiceLinePct: number | null;
  successPct: number | null;
  serviceLine: { state: ThresholdState; depthM: number | null };
  successThreshold: { state: ThresholdState; depthM: number | null };
}

const ratio = (sum: number, count: number): number | null =>
  count > 0 && Number.isFinite(sum) ? sum / count : null;

const threshold = (
  records: LobAggregate[],
  pick: (record: LobAggregate) => number,
): { state: ThresholdState; depthM: number | null } => {
  if (!records.length) return { state: 'unavailable', depthM: null };
  const values = records.map(pick);
  const first = values[0];
  if (!Number.isFinite(first)) return { state: 'unavailable', depthM: null };
  if (values.some((value) => value !== first)) {
    return { state: 'incompatible', depthM: null };
  }
  return { state: 'compatible', depthM: first };
};

const recordHasMeasurements = (record: LobAggregate): boolean =>
  record.n_accepted_shots > 0 ||
  record.n_with_height > 0 ||
  record.n_with_position > 0 ||
  record.n_successful > 0;

const emptyLobAnalytics = (
  availability: LobAnalytics['availability'],
): LobAnalytics => ({
  hasData: false,
  availability,
  averageHeightM: null,
  averageDepthM: null,
  behindServiceLinePct: null,
  inFrontServiceLinePct: null,
  successPct: null,
  serviceLine: { state: 'unavailable', depthM: null },
  successThreshold: { state: 'unavailable', depthM: null },
});

function recordsForPlayer(
  lobs: NonNullable<NonNullable<PlayerStatistics['shots_tracking']>['lobs']>,
  selection: LobSelection,
): LobAggregate[] {
  if (selection !== 'both') {
    const record = lobs[selection];
    return record ? [record] : [];
  }

  // A combined record is authoritative for that player. Subtypes are only a
  // fallback representation when the combined record is absent.
  if (Object.prototype.hasOwnProperty.call(lobs, 'lob') && lobs.lob) {
    return [lobs.lob];
  }
  return [lobs.forehand_lob, lobs.backhand_lob].filter(
    (record): record is LobAggregate => !!record,
  );
}

/** One comparison row's source players: a single player, or a team's members. */
export interface LobGroup {
  id: string;
  playerIds: string[];
}

export interface LobGroupMetrics extends LobGroup {
  metrics: LobAnalytics | null;
}

/**
 * Aggregate lobs INSIDE each group only. Groups never pool into each other, so
 * a player row stays one player and a team row weights just that team's two
 * members. Groups without usable records are kept (not dropped) so the caller
 * can still label them.
 */
export function buildLobGroupMetrics(
  groups: LobGroup[],
  players: Record<string, PlayerStatistics>,
  selection: LobSelection,
): LobGroupMetrics[] {
  return groups.map((group) => ({
    ...group,
    metrics: aggregateLobMetrics(group.playerIds, players, selection),
  }));
}

/** Aggregate backend lob sums/counts without reclassifying or averaging rates. */
export function aggregateLobMetrics(
  playerIds: string[],
  players: Record<string, PlayerStatistics>,
  selection: LobSelection,
): LobAnalytics | null {
  const selected = playerIds.map((id) => players[id]).filter(Boolean);
  const hasLobContainer = selected.some((ps) => {
    const lobs = ps.shots_tracking?.lobs;
    return !!lobs && Object.keys(lobs).length > 0;
  });
  if (!hasLobContainer) return null;

  const recordsByPlayer = selected.map((ps) => {
    const lobs = ps.shots_tracking?.lobs;
    return lobs ? recordsForPlayer(lobs, selection) : [];
  });
  const records = recordsByPlayer.flat();
  const availablePlayers = recordsByPlayer.filter(
    (playerRecords) => playerRecords.length > 0,
  ).length;
  const availability =
    availablePlayers === selected.length ? 'complete' : 'partial';
  if (!records.length) return emptyLobAnalytics('none');

  const hasData = records.some(recordHasMeasurements);
  if (!hasData) return emptyLobAnalytics(availability);

  const serviceLine = threshold(
    records,
    (record) => record.service_line_depth_m,
  );
  const successThreshold = threshold(
    records,
    (record) => record.successful_min_depth_m,
  );
  const nWithHeight = records.reduce(
    (sum, record) => sum + record.n_with_height,
    0,
  );
  const nWithPosition = records.reduce(
    (sum, record) => sum + record.n_with_position,
    0,
  );
  const nAccepted = records.reduce(
    (sum, record) => sum + record.n_accepted_shots,
    0,
  );

  return {
    hasData: true,
    availability,
    averageHeightM: ratio(
      records.reduce((sum, record) => sum + record.height_sum_m, 0),
      nWithHeight,
    ),
    averageDepthM: ratio(
      records.reduce((sum, record) => sum + record.depth_sum_m, 0),
      nWithPosition,
    ),
    behindServiceLinePct:
      serviceLine.state === 'compatible'
        ? ratio(
            records.reduce(
              (sum, record) => sum + record.n_above_service_line,
              0,
            ),
            nWithPosition,
          )
        : null,
    inFrontServiceLinePct:
      serviceLine.state === 'compatible'
        ? ratio(
            records.reduce(
              (sum, record) => sum + record.n_below_service_line,
              0,
            ),
            nWithPosition,
          )
        : null,
    successPct:
      successThreshold.state === 'compatible'
        ? ratio(
            records.reduce((sum, record) => sum + record.n_successful, 0),
            nAccepted,
          )
        : null,
    serviceLine,
    successThreshold,
  };
}
